import { z } from 'zod';

// Order ID validation
const OrderIdSchema = z.string()
  .min(1, { message: "Order ID is required" })
  .max(50, { message: "Order ID cannot exceed 50 characters" });

// Merchant ID validation
const MerchantIdSchema = z.string()
  .min(1, { message: "Merchant ID must not be empty if provided" })
  .max(16, { message: "Merchant ID cannot exceed 16 characters" })
  .regex(/^\d+$/, { message: "Merchant ID must contain only digits" })
  .optional();

// Set parameter validation (empty string or "1")
const SetParameterSchema = z.string()
  .max(1, { message: "Set parameter cannot exceed 1 character" })
  .regex(/^(|1)$/, { message: "Set parameter must be empty string or '1'" });

// Main inquire by order ID request schema (path parameters only)
const InquireByOrderidRequestSchema = z.object({
  // Required path parameters
  orderid: OrderIdSchema,
  merchid: MerchantIdSchema,
  set: SetParameterSchema,
  // OrderUp provider routing — declared so the strict schema does NOT reject
  // them, letting the neutral route resolve the issuing provider.
  tenantId: z.number().int().optional(),
  restaurantId: z.number().int().optional()
}).strict();

// Strict schema requiring enhanced validation
const StrictInquireByOrderidRequestSchema = InquireByOrderidRequestSchema.safeExtend({
  orderid: OrderIdSchema.refine((val) => val.length >= 5, {
    message: "Order ID should be at least 5 characters"
  }),
  merchid: MerchantIdSchema.refine((val) => !val || val.length >= 10, {
    message: "Merchant ID should be at least 10 characters"
  })
});

// Path parameters schema (alias for clarity)
const InquireByOrderidPathParamsSchema = InquireByOrderidRequestSchema;

// Query string schema (for URL parsing)
const InquireByOrderidRequestQuerySchema = z.object({
  orderid: z.string()
    .min(1, { message: "Order ID is required" })
    .max(50, { message: "Order ID cannot exceed 50 characters" }),
  merchid: z.string()
    .min(1, { message: "Merchant ID must not be empty if provided" })
    .max(16, { message: "Merchant ID cannot exceed 16 characters" })
    .optional(),
  set: z.string()
    .max(1, { message: "Set parameter cannot exceed 1 character" })
    .optional()
    .default('')
})
.transform((data) => ({
  orderid: data.orderid.trim(),
  ...(data.merchid && { merchid: data.merchid.trim() }),
  set: data.set || ''
}));

// Path validation schema (for route parameters)
const InquireByOrderidPathSchema = z.object({
  orderid: z.string()
    .min(1, { message: "Order ID path parameter is required" })
    .max(50, { message: "Order ID path parameter cannot exceed 50 characters" }),
  merchid: z.string()
    .min(1, { message: "Merchant ID path parameter is required" })
    .max(16, { message: "Merchant ID path parameter cannot exceed 16 characters" })
    .regex(/^\d+$/, { message: "Merchant ID path parameter must contain only digits" }),
  set: z.string()
    .max(1, { message: "Set path parameter cannot exceed 1 character" })
    .regex(/^(|1)$/, { message: "Set path parameter must be empty string or '1'" })
    .optional()
    .default('')
});

// Validation helpers
const validateOrderId = (orderid: string): boolean => {
  return orderid.length > 0 && orderid.length <= 50;
};

const validateMerchantId = (merchid: string): boolean => {
  return merchid.length > 0 && merchid.length <= 16 && /^\d+$/.test(merchid);
};

const validateSetParameter = (set: string): boolean => {
  return set.length <= 1 && (set === '' || set === '1');
};

const validateInquireByOrderidRequest = (data: any): boolean => {
  return validateOrderId(data.orderid) && 
         validateMerchantId(data.merchid) && 
         validateSetParameter(data.set);
};

// Restricted inquiry schema (set = "1")
const RestrictedInquireByOrderidSchema = InquireByOrderidRequestSchema.safeExtend({
  set: z.literal('1')
});

// Unrestricted inquiry schema (set = "")
const UnrestrictedInquireByOrderidSchema = InquireByOrderidRequestSchema.safeExtend({
  set: z.literal('')
});

// Type exports
export type InquireByOrderidRequest = z.infer<typeof InquireByOrderidRequestSchema>;
export type StrictInquireByOrderidRequest = z.infer<typeof StrictInquireByOrderidRequestSchema>;
export type InquireByOrderidPathParams = z.infer<typeof InquireByOrderidPathParamsSchema>;
export type InquireByOrderidRequestQuery = z.infer<typeof InquireByOrderidRequestQuerySchema>;
export type InquireByOrderidPath = z.infer<typeof InquireByOrderidPathSchema>;
export type RestrictedInquireByOrderidRequest = z.infer<typeof RestrictedInquireByOrderidSchema>;
export type UnrestrictedInquireByOrderidRequest = z.infer<typeof UnrestrictedInquireByOrderidSchema>;

// Schema exports
export {
  InquireByOrderidRequestSchema,
  StrictInquireByOrderidRequestSchema,
  InquireByOrderidPathParamsSchema,
  InquireByOrderidRequestQuerySchema,
  InquireByOrderidPathSchema,
  RestrictedInquireByOrderidSchema,
  UnrestrictedInquireByOrderidSchema,
  OrderIdSchema,
  MerchantIdSchema,
  SetParameterSchema,
  validateOrderId,
  validateMerchantId,
  validateSetParameter,
  validateInquireByOrderidRequest
};

// Default export
export default InquireByOrderidRequestSchema; 